import Big from 'big.js';
import { GetOptionPrice, GetPrice } from 'smg/customer-api/lineitems';
import { GetStockItemPrice } from 'smg/customer-api/stockitems';
import { IsObject } from 'smg/object';
import { DollarFormat, RemToPx } from 'smg/util';

declare interface myWindow extends Window {
    readonly customStage: boolean;
}
declare const window: myWindow;

export function InitGallery() {
    const $stage = document.querySelector('#stage') as HTMLElement;

    const $galleryContainer = document.querySelector<HTMLElement>('#gallery-image-container');
    if (!$galleryContainer) {
        return;
    }
    const $galleryContainer2 = $galleryContainer;
    const $imgs = $galleryContainer.querySelectorAll<HTMLImageElement>('.gallery-image');

    if (!window.customStage) {
        $imgs.forEach($img => {
            const $a = $img.closest('a') as HTMLAnchorElement;
            const $big = $a.nextElementSibling as HTMLAnchorElement;
            $img.addEventListener('mouseover', () => {
                $stage.innerHTML = '';
                $big.classList.remove('d-none');
                $stage.appendChild($big);
            });
        });

        $imgs[0].dispatchEvent(new MouseEvent('mouseover'));
    }

    const $scrollUpBtn = document.querySelector('#scroll-up-btn') as HTMLElement;
    const $scrollDownBtn = document.querySelector('#scroll-down-btn') as HTMLElement;
    const $scrollLeftBtn = document.querySelector('#scroll-left-btn') as HTMLElement;
    const $scrollRightBtn = document.querySelector('#scroll-right-btn') as HTMLElement;

    const scrollBtns = [$scrollUpBtn, $scrollDownBtn, $scrollLeftBtn, $scrollRightBtn];

    for (const btn of scrollBtns) {
        btn.addEventListener('click', e => {
            e.preventDefault();

            switch (btn) {
            case $scrollUpBtn:
                scrollGallery({ top: -100 }, 'smooth');
                break;
            case $scrollDownBtn:
                scrollGallery({ top: 100 }, 'smooth');
                break;
            case $scrollLeftBtn:
                scrollGallery({ left: -100 }, 'smooth');
                break;
            case $scrollRightBtn:
                scrollGallery({ left: 100 }, 'smooth');
                break;
            }
        });
    }

    function scrollGallery(opts: ScrollToOptions, behavior?: ScrollBehavior) {
        $galleryContainer2.scrollBy({
            top: opts.top,
            left: opts.left,
            behavior,
        });
    }
}

export function InitSeeMore() {
    const $description = document.querySelector('#product-description') as HTMLElement;

    const $seeMore = document.querySelector('#product-description-see-more') as HTMLElement;
    const $seeLess = document.querySelector('#product-description-see-less') as HTMLElement;

    const checkScrollHeight = () => {
        if ($description.scrollHeight > $description.clientHeight + RemToPx(2.5)) {
            $description.classList.add('collapsed');
        }
    };

    window.addEventListener('resize', checkScrollHeight);
    checkScrollHeight();

    const $stageContainer = document.querySelector('#stage-container') as HTMLElement;
    const $galleryContainer = document.querySelector<HTMLElement>('#gallery-container');

    $seeMore.addEventListener('click', e => {
        e.preventDefault();

        const prevDescHeight = $description.clientHeight;

        $stageContainer.style.height = `${$stageContainer.clientHeight}px`;
        $stageContainer.classList.remove('flex-grow-1');
        $galleryContainer?.classList.add('flex-grow-1');

        $description.classList.remove('collapsed');
        $description.classList.add('expanded');

        window.scrollBy({
            top: $description.clientHeight - prevDescHeight,
            behavior: 'smooth',
        });
    });

    $seeLess.addEventListener('click', e => {
        e.preventDefault();

        const prevDescHeight = $description.clientHeight;

        $stageContainer.style.height = '';
        $stageContainer.classList.add('flex-grow-1');
        $galleryContainer?.classList.remove('flex-grow-1');

        $description.classList.remove('expanded');
        $description.classList.add('collapsed');

        window.scrollBy({
            top: $description.clientHeight - prevDescHeight,
            behavior: 'smooth',
        });
    });
}

export async function InitPrices(stockItem?: boolean, omitCents?: boolean) {
    for (const $price of Array.from(document.querySelectorAll<HTMLElement>('[data-quantity]'))) {
        let calculatedPrice = new Big('0');

        if (stockItem) {
            const stockItemID = $price.getAttribute('data-stock-item-id');
            calculatedPrice = await GetStockItemPrice(
                stockItemID ?? '',
                parseInt($price.getAttribute('data-quantity') || '0', 10),
            );
        } else {
            const thisLineItem = $price.getAttribute('data-line-item') ?? '';
            const thisLineItemType = $price.getAttribute('data-line-item-type') ?? '';
            const thisSize = $price.getAttribute('data-size') ?? '';
            const thisQuantity = parseInt($price.getAttribute('data-quantity') || '0', 10);

            const unitPrice = await GetPrice(
                thisLineItem,
                thisLineItemType,
                thisSize,
                thisQuantity,
            );

            let optionsPrice = new Big(0);

            interface childLineItem {
                child: string;
                option: string;
            }

            const childArray = $price.getAttribute('data-child-line-items') ?? '';
            const children = JSON.parse(childArray) as childLineItem[];

            if (Array.isArray(children) && childArray.length && IsObject(children[0])) {
                for (const i of children) {
                    optionsPrice = optionsPrice.add(await GetOptionPrice(
                        thisLineItem,
                        i.child,
                        i.option,
                    ),
                    );
                }
            }

            calculatedPrice = unitPrice.add(optionsPrice);
        }

        let dollars = DollarFormat(calculatedPrice);
        if (omitCents) {
            dollars = dollars.slice(0, -3);
        }
        $price.innerText = dollars;

        if (!$price.classList.contains('price-starts-at')) {
            $price.innerText += ' each';
        }

        $price.classList.remove('placeholder-wave');
    }
}

export async function WhiteLayer(dataURL: string): Promise<string> {
    const img = new Image();
    await new Promise(resolve => {
        img.onload = resolve;
        img.src = dataURL;
    });

    const $canvas = document.createElement('canvas');
    const ctx = $canvas.getContext('2d') as CanvasRenderingContext2D;

    $canvas.width = img.naturalWidth;
    $canvas.height = img.naturalHeight;

    ctx.drawImage(img, 0, 0);

    const imageData = ctx.getImageData(0, 0, $canvas.width, $canvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] >= (255 / 2)) {
            data[i + 3] = 255;

            data[i] = 0;
            data[i + 1] = 0;
            data[i + 2] = 0;
        } else {
            data[i + 3] = 255;

            data[i] = 255;
            data[i + 1] = 255;
            data[i + 2] = 255;
        }
    }
    ctx.putImageData(imageData, 0, 0);

    return $canvas.toDataURL('image/jpeg', 1);
}

export async function ColorLayer(dataURL: string): Promise<string> {
    const img = new Image();
    await new Promise(resolve => {
        img.onload = resolve;
        img.src = dataURL;
    });

    const $canvas = document.createElement('canvas');
    const ctx = $canvas.getContext('2d') as CanvasRenderingContext2D;

    $canvas.width = img.naturalWidth;
    $canvas.height = img.naturalHeight;

    ctx.drawImage(img, 0, 0);

    const imageData = ctx.getImageData(0, 0, $canvas.width, $canvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] >= (255 / 2)) {
            data[i + 3] = 255;
        } else {
            data[i + 3] = 255;

            data[i] = 255;
            data[i + 1] = 255;
            data[i + 2] = 255;
        }
    }
    ctx.putImageData(imageData, 0, 0);

    return $canvas.toDataURL('image/jpeg', 1);
}